import { useCallback } from 'react';
import { useTracerContext } from '../../tracer/TracerProvider';
import { SPAN_SCOPE } from '../../tracer/config/spanScope';
import { TRACER_STATUS_CODE } from '../../tracer/utils/tracerStatusCode';

const APP_EVENT_PROCESSING_TIMEOUT = 60 * 1000;

export const useChatAgentTracer = () => {
    const {
        createRootSpan,
        getRootSpan,
        endRootSpan,
        startSpan,
        endSpan,
        getSpan,
        setSpanStatus,
        getTraceRequestHeader,
    } = useTracerContext();

    const endTrace = useCallback(
        (status = TRACER_STATUS_CODE.OK, message) => {
            const rootSpan = getRootSpan(SPAN_SCOPE.CHAT_AGENT_SCOPE);
            if (!rootSpan) {
                return;
            }

            if (rootSpan.timeoutId) {
                clearTimeout(rootSpan.timeoutId);
            }

            setSpanStatus(rootSpan, status, message || status);
            endRootSpan(SPAN_SCOPE.CHAT_AGENT_SCOPE);
        },
        [getRootSpan, endRootSpan, setSpanStatus]
    );

    const startTrace = useCallback(
        ({ event, appEventProcessingTimeout = APP_EVENT_PROCESSING_TIMEOUT } = {}) => {
            const eventType = event?.event?.type;
            if (!eventType) {
                return null;
            }

            endTrace(TRACER_STATUS_CODE.UNSET, 'superseded_by_new_event');

            const rootSpan = createRootSpan(SPAN_SCOPE.CHAT_AGENT_SCOPE, {
                attributes: {
                    spanScope: SPAN_SCOPE.CHAT_AGENT_SCOPE,
                    eventType,
                    module: event.appContext?.module,
                    triggerName: event.event?.triggerName,
                    eventId: event.id,
                    accountId: event.appContext?.accountId,
                    entityId: event.formContext?.entityId,
                    userId: event.appContext?.userId,
                    sessionId: event.sessionId,
                    tabId: event.tabId,
                    feVersion: event.feVersion,
                    gwVersion: event.gwVersion,
                    currentUrl: event.appContext?.metadata?.currentUrl,
                },
            });

            if (rootSpan) {
                rootSpan.isFirstWsMessage = true;

                if (appEventProcessingTimeout > 0) {
                    rootSpan.timeoutId = setTimeout(() => {
                        const rootSpan = getRootSpan(SPAN_SCOPE.CHAT_AGENT_SCOPE);
                        if (rootSpan) {
                            setSpanStatus(rootSpan, TRACER_STATUS_CODE.ERROR, 'Timeout');
                            endRootSpan(SPAN_SCOPE.CHAT_AGENT_SCOPE);
                        }
                    }, appEventProcessingTimeout);
                }
            }

            return rootSpan ? SPAN_SCOPE.CHAT_AGENT_SCOPE : null;
        },
        [endTrace, createRootSpan, getRootSpan, setSpanStatus, endRootSpan]
    );

    const getTraceHeaders = useCallback(
        () => getTraceRequestHeader(getRootSpan(SPAN_SCOPE.CHAT_AGENT_SCOPE)),
        [getRootSpan, getTraceRequestHeader]
    );

    const addTraceAttributes = useCallback(
        attrs => {
            const span = getRootSpan(SPAN_SCOPE.CHAT_AGENT_SCOPE);
            if (span && attrs) {
                span.setAttributes(attrs);
            }
        },
        [getRootSpan]
    );

    const getSpanContext = useCallback(() => {
        const span = getRootSpan(SPAN_SCOPE.CHAT_AGENT_SCOPE);
        return span ? span.spanContext() : null;
    }, [getRootSpan]);

    const startChildSpan = useCallback(
        (name, attributes = {}) => {
            const root = getRootSpan(SPAN_SCOPE.CHAT_AGENT_SCOPE);
            if (!root) {
                return null;
            }

            const childName = `${name}-${Date.now()}`;

            startSpan(childName, root, {
                ...attributes,
            });
            return childName;
        },
        [getRootSpan, startSpan]
    );

    const endChildSpan = useCallback(
        (childName, status = TRACER_STATUS_CODE.OK, message) => {
            const span = getSpan(childName);
            if (!span) {
                return;
            }

            if (status != null) {
                setSpanStatus(span, status, message || status);
            }
            endSpan(childName);
        },
        [getSpan, endSpan, setSpanStatus]
    );

    const addEvent = useCallback(
        (name, attributes) => {
            const root = getRootSpan(SPAN_SCOPE.CHAT_AGENT_SCOPE);
            if (!root) {
                return null;
            }

            const isFirstMessage = root.isFirstWsMessage === true;
            root.isFirstWsMessage = false;

            root.addEvent(name, { ...attributes, isFirstMessage });
        },
        [getRootSpan]
    );

    return {
        startTrace,
        endTrace,
        getSpanContext,
        getTraceHeaders,
        addTraceAttributes,
        startChildSpan,
        endChildSpan,
        addEvent,
    };
};
